import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';

import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { MemberNameLink } from '@/components/shared/member-name-link';
import { PageHeader } from '@/components/shared/page-header';
import { Pagination } from '@/components/shared/pagination';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { KioskScreenLink } from '@/pages/attendance/kiosk-screen-link';
import { ApiError } from '@/lib/api-errors';
import { hasPermission, PERMISSIONS } from '@/lib/permissions';
import { useChurchStore } from '@/stores/church-store';
import { usePreferences } from '@/stores/preferences-store';
import { formatDate, formatDateTime, formatTime } from '@/utils/format';

interface Session {
  id: string;
  occurrenceId: string;
  method: string;
  status: 'open' | 'closed';
  headcount: number;
  opensAt: string;
  closesAt: string;
  kiosks: Array<{ id: string; name: string; pairingUrl: string }>;
}

interface Occurrence {
  id: string;
  programName: string | null;
  programKind: 'service' | 'program' | null;
  occurrenceDate: string;
  startsAt: string;
  venueName: string | null;
}

interface CheckIn {
  id: string;
  memberId: string | null;
  method: string;
  checkedInAt: string;
  kioskId: string | null;
  kioskName: string | null;
}

interface MemberSummary {
  id: string;
  firstName: string;
  lastName: string;
}

const CheckInMemberCell = ({ memberId }: { memberId: string | null }) => {
  const { t } = useTranslation('attendance');
  const member = useQuery({
    queryKey: ['people', 'members', memberId],
    queryFn: () => api.get<MemberSummary>(`/people/members/${memberId}`),
    enabled: Boolean(memberId),
  });

  if (!memberId) {
    return <span className="text-fg-secondary">{t('sessions.detail.guest')}</span>;
  }

  if (member.isLoading) {
    return <Skeleton className="h-4 w-32" />;
  }

  const name = member.data
    ? `${member.data.firstName} ${member.data.lastName}`.trim()
    : t('sessions.detail.unknownMember');

  return <MemberNameLink memberId={memberId} name={name} className="font-medium" />;
};

export const SessionDetailPage = () => {
  const { sessionId = '' } = useParams();
  const { t } = useTranslation('attendance');
  const { t: tPrograms } = useTranslation('programs');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const { locale } = usePreferences();
  const membership = useChurchStore((state) => state.membership);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [closeOpen, setCloseOpen] = useState(false);
  const [checkInPage, setCheckInPage] = useState(1);
  const [checkInPageSize, setCheckInPageSize] = useState(25);

  const canClose = hasPermission(membership, PERMISSIONS.ATTENDANCE_MANAGE_SESSIONS);

  const session = useQuery({
    queryKey: ['attendance-sessions', sessionId],
    queryFn: () => api.get<Session>(`/attendance/sessions/${sessionId}`),
    enabled: Boolean(sessionId),
    refetchInterval: 15_000,
  });

  const occurrence = useQuery({
    queryKey: ['occurrences', session.data?.occurrenceId],
    queryFn: () => api.get<Occurrence>(`/occurrences/${session.data!.occurrenceId}`),
    enabled: Boolean(session.data?.occurrenceId),
  });

  const checkIns = useQuery({
    queryKey: ['attendance-sessions', sessionId, 'check-ins', checkInPage, checkInPageSize],
    queryFn: () => {
      const params = new URLSearchParams({
        page: String(checkInPage),
        pageSize: String(checkInPageSize),
      });
      return api.get<{ data: CheckIn[]; page: { page: number; pageSize: number; totalItems: number; totalPages: number } }>(
        `/attendance/sessions/${sessionId}/check-ins?${params}`,
      );
    },
    enabled: Boolean(sessionId),
    refetchInterval: 15_000,
  });

  const closeSession = useMutation({
    mutationFn: () => api.post<Session>(`/attendance/sessions/${sessionId}/close`),
    onSuccess: () => {
      toast.success(t('sessions.closed'));
      void queryClient.invalidateQueries({ queryKey: ['attendance-sessions'] });
      void navigate('/attendance');
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  if (session.isLoading) {
    return (
      <div className="space-y-4">
        <PageHeader
          title={t('sessions.title')}
          breadcrumbs={[
            { label: t('sessions.title'), to: '/attendance' },
            { label: tCommon('state.loading') },
          ]}
        />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  const row = session.data;
  if (!row) {
    return (
      <div className="space-y-4">
        <PageHeader
          title={t('sessions.detail.notFound')}
          description={t('sessions.detail.notFoundBody')}
          breadcrumbs={[
            { label: t('sessions.title'), to: '/attendance' },
            { label: t('sessions.detail.notFound') },
          ]}
        />
      </div>
    );
  }

  const occ = occurrence.data;
  const kind = occ?.programKind ?? 'service';
  const kindLabel = tPrograms(`calendar.kind.${kind}`);
  const gatheringName = occ?.programName?.trim() || kindLabel;
  const whenLine = occ
    ? `${formatDate(occ.occurrenceDate, { locale, style: 'medium' })} · ${formatTime(occ.startsAt, { locale })}${occ.venueName ? ` · ${occ.venueName}` : ''}`
    : null;

  const checkInColumns: DataTableColumn<CheckIn>[] = [
    {
      id: 'checkedInAt',
      header: t('sessions.columns.checkedInAt'),
      cell: (checkIn) => formatDateTime(checkIn.checkedInAt, { locale }),
    },
    {
      id: 'member',
      header: t('sessions.columns.member'),
      cell: (checkIn) => <CheckInMemberCell memberId={checkIn.memberId} />,
    },
    {
      id: 'screen',
      header: t('sessions.columns.screen'),
      cell: (checkIn) =>
        checkIn.kioskName ? (
          <span className="text-body-sm text-fg">{checkIn.kioskName}</span>
        ) : (
          <span className="text-body-sm text-fg-secondary">—</span>
        ),
    },
    {
      id: 'method',
      header: t('sessions.columns.method'),
      cell: (checkIn) => t(`sessions.method.${checkIn.method}`, checkIn.method),
    },
  ];

  return (
    <div className="space-y-4">
      <PageHeader
        title={gatheringName}
        description={whenLine ?? t(`sessions.method.${row.method}`, row.method)}
        breadcrumbs={[
          { label: t('sessions.title'), to: '/attendance' },
          { label: gatheringName },
        ]}
        headerActions={
          row.status === 'open' && canClose ? (
            <Button variant="outline" onClick={() => setCloseOpen(true)}>
              {t('sessions.close')}
            </Button>
          ) : undefined
        }
      />

      <ConfirmDialog
        open={closeOpen}
        onOpenChange={setCloseOpen}
        title={t('confirm.closeSession.title')}
        description={t('confirm.closeSession.description')}
        confirmLabel={t('confirm.closeSession.confirm')}
        cancelLabel={tCommon('action.cancel')}
        loading={closeSession.isPending}
        onConfirm={() => closeSession.mutate()}
      />

      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          <div>
            <CardTitle>{t('sessions.detail.summaryTitle')}</CardTitle>
            <p className="mt-1 text-body-sm text-fg-secondary">
              {t(`sessions.method.${row.method}`, row.method)}
            </p>
          </div>
          <Badge variant={row.status === 'open' ? 'success' : 'neutral'}>
            {row.status === 'open' ? t('sessions.statusOpen') : t('sessions.statusClosed')}
          </Badge>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-body-lg font-semibold tabular-nums text-fg">
            {t('sessions.headcountLive', { count: row.headcount })}
          </p>
          {row.method === 'qr' ? (
            <div>
              <p className="text-body-sm font-medium text-fg">{t('sessions.detail.screensTitle')}</p>
              {row.kiosks.length > 0 ? (
                <ul className="mt-2 flex flex-wrap gap-2">
                  {row.kiosks.map((kiosk) => (
                    <li key={kiosk.id}>
                      <KioskScreenLink id={kiosk.id} name={kiosk.name} />
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="mt-1 text-body-sm text-fg-secondary">{t('sessions.detail.noScreens')}</p>
              )}
            </div>
          ) : null}
        </CardContent>
      </Card>

      <DataTable
        rows={checkIns.data?.data ?? []}
        columns={checkInColumns}
        rowKey={(checkIn) => checkIn.id}
        loading={checkIns.isLoading}
        empty={
          <p className="text-body-sm text-fg-secondary">{t('sessions.detail.checkInsEmpty')}</p>
        }
      />

      {checkIns.data?.page && checkIns.data.page.totalPages > 1 ? (
        <Pagination
          page={checkIns.data.page.page}
          pageSize={checkIns.data.page.pageSize}
          totalItems={checkIns.data.page.totalItems}
          totalPages={checkIns.data.page.totalPages}
          onPageChange={setCheckInPage}
          onPageSizeChange={(next) => {
            setCheckInPageSize(next);
            setCheckInPage(1);
          }}
          labels={{
            showing: ({ from, to, total }) => tCommon('pagination.showing', { from, to, total }),
            perPage: tCommon('pagination.perPage'),
            first: tCommon('pagination.first'),
            previous: tCommon('pagination.previous'),
            next: tCommon('pagination.next'),
            last: tCommon('pagination.last'),
          }}
        />
      ) : null}
    </div>
  );
};
